import React from "react";
import { useTranslation } from "react-i18next";
import {
  FunnelStateEnum,
  FunnelStateTransitionDate,
} from "@/__generated__/types";
import {
  FunnelStateMaps,
  FUNNEL_STATE_TRANSLATION_KEYS,
  FunnelStateEnumToGroup,
} from "@/util/funnelGroups";

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@radix-ui/react-tooltip";

interface StudentFunnelStateSectionProps {
  funnelState: FunnelStateEnum;
  funnelStateTransitionSummary: FunnelStateTransitionDate[] | null;
}

interface FunnelSection {
  id: string;
  label: string;
  isCompleted: boolean;
  date?: string | null;
  color: string;
}

const FUNNEL_GROUP_COLORS = {
  inquired: {
    background: "bg-violet-50",
    border: "border-violet-200",
    text: "text-violet-800",
  },
  applied: {
    background: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-800",
  },
  admitted: {
    background: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-800",
  },
  committed: {
    background: "bg-green-50",
    border: "border-green-200",
    text: "text-green-800",
  },
} as const;

export const StudentFunnelStateSection: React.FC<
  StudentFunnelStateSectionProps
> = ({ funnelState, funnelStateTransitionSummary }) => {
  const { t } = useTranslation();

  const { inquired, applied, admitted, committed } =
    FunnelStateMaps.STUDENT_PROFILE;

  const isInquiredCompleted = [
    ...inquired,
    ...applied,
    ...admitted,
    ...committed,
  ].includes(funnelState);

  const isAppliedCompleted = [...applied, ...admitted, ...committed].includes(
    funnelState,
  );

  const isAdmittedCompleted = [...admitted, ...committed].includes(funnelState);

  const isCommittedCompleted = committed.includes(funnelState);

  const getDateForState = (state: FunnelStateEnum) => {
    return funnelStateTransitionSummary?.find(
      (transition) => transition.state === state,
    )?.date;
  };

  const funnelSections: FunnelSection[] = [
    {
      id: "inquired",
      label: t("funnelGroups.inquired"),
      isCompleted: isInquiredCompleted,
      date: getDateForState(FunnelStateEnum.Inquired),
      color: "--color-chart-3",
    },
    {
      id: "applied",
      label: t("funnelGroups.applied"),
      isCompleted: isAppliedCompleted,
      date: getDateForState(FunnelStateEnum.Applied),
      color: "--color-chart-1",
    },
    {
      id: "admitted",
      label: t("funnelGroups.admitted"),
      isCompleted: isAdmittedCompleted,
      date: getDateForState(FunnelStateEnum.Accepted),
      color: "--color-chart-4",
    },
    {
      id: "committed",
      label: t("funnelGroups.committed"),
      isCompleted: isCommittedCompleted,
      date: getDateForState(FunnelStateEnum.Committed),
      color: "--color-chart-2",
    },
  ];

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const badgeText = t(
    `global.funnelStates.${FUNNEL_STATE_TRANSLATION_KEYS[funnelState]}`,
  ).toUpperCase();
  const funnelGroups =
    FunnelStateEnumToGroup.STUDENT_PROFILE[funnelState] || [];
  const funnelGroup = funnelGroups[0] || "inquired";
  const badgeColors =
    FUNNEL_GROUP_COLORS[funnelGroup as keyof typeof FUNNEL_GROUP_COLORS] ||
    FUNNEL_GROUP_COLORS.inquired;

  return (
    <div className="w-full">
      <div className="flex items-start justify-between">
        <h3 className="text-[16px] leading-tight font-semibold">
          {t("studentCard.funnelState.title")}
        </h3>
        <div
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs leading-4 font-semibold ${badgeColors.background} ${badgeColors.border} ${badgeColors.text}`}
          style={{ fontFamily: "Inter" }}
        >
          {badgeText}
        </div>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        {t("studentCard.funnelState.subtitle")}
      </p>
      <div className="rounded-lg border border-border bg-card px-6 py-6 pb-7 shadow-sm">
        <div className="flex gap-2">
          {funnelSections.map((section) => (
            <div key={section.id} className="relative flex-1">
              {/* Section Label */}
              <div className="mb-3 text-left">
                <span
                  className={`text-sm font-medium ${
                    section.isCompleted
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {section.label}
                </span>
              </div>

              {/* Progress Bar */}
              {section.isCompleted ? (
                section.date ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        style={{
                          backgroundColor: `var(${section.color})`,
                        }}
                        className={`h-2 w-full rounded-full`}
                      ></div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="rounded-md border border-gray-200 bg-white p-2 text-black"
                    >
                      <div className="text-center">
                        <span className="text-sm font-medium text-foreground">
                          {section.label}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(section.date)}
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div
                    style={{
                      backgroundColor: `var(${section.color})`,
                    }}
                    className={`h-2 w-full rounded-full`}
                  ></div>
                )
              ) : (
                <div className="h-2 w-full rounded-full bg-background-light" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
